import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * A labelled, expandable pill in the hub's in-page navigation. The label stays visible: the
 * chevron only indicates that sub-sections are available. Every child scrolls to a section that
 * already exists on the page.
 */
export const SectionNavMenu = ({ label, items, onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (!wrap.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Mouse only: a touch tap also fires pointerenter, and hovering there would fight the tap.
  const hoverOpen = (e) => {
    if (e.pointerType !== "mouse") return;
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  // The delay covers the gap between the pill and the panel, and slips between menu options.
  const hoverClose = (e) => {
    if (e.pointerType !== "mouse") return;
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };

  const choose = (target) => {
    clearTimeout(closeTimer.current);
    setOpen(false);
    onSelect(target);
  };

  return (
    <div
      ref={wrap}
      className="relative"
      onPointerEnter={hoverOpen}
      onPointerLeave={hoverClose}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          clearTimeout(closeTimer.current);
          setOpen((v) => !v);
        }}
        data-testid={`hub-section-nav-menu-${label.toLowerCase()}`}
        className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[13px] md:text-[13.5px] font-medium transition-colors duration-300 ${
          open
            ? "border-sage bg-sage text-white"
            : "border-charcoal/15 bg-white text-charcoal hover:border-sage hover:bg-sage hover:text-white"
        }`}
      >
        {label}
        <ChevronDown
          size={15}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <>
          {/* Keeps the pointer inside the menu while it crosses the gap to the panel. */}
          <span aria-hidden="true" className="absolute left-0 right-0 top-full h-2" />
          <div
            role="menu"
            data-testid={`hub-section-nav-panel-${label.toLowerCase()}`}
            className="absolute left-0 top-[calc(100%+8px)] z-30 min-w-[232px] border border-line bg-white py-1.5 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.45)]"
          >
            {items.map((c) => (
              <button
                key={c.target}
                type="button"
                role="menuitem"
                onClick={() => choose(c.target)}
                data-testid={`hub-section-nav-${c.target}`}
                className="block w-full text-left px-4 py-2.5 text-[13.5px] font-medium text-charcoal border-l-2 border-transparent hover:border-sage hover:bg-bone hover:text-sage-dark focus:outline-none focus-visible:border-sage focus-visible:bg-bone transition-colors duration-200"
              >
                {c.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
